/**
 * The swap composer's arithmetic and its one outside call.
 *
 * The estimate exists so staff can check that what they typed is the swap they
 * meant ("50 USDC should come out near 50 USDT"). It is not a price promise:
 * the member gets their own quote when they open the swap, which can differ.
 * So it shows exactly what the app's pair view shows (a dollar value and a
 * "~" token amount) and nothing more, never a rate, route, minimum or fee.
 */

/** "50.5" at 18 decimals → "50500000000000000000". Refuses excess places. */
export function toBaseUnits(amount: string, decimals: number): string {
  if (!/^\d+(\.\d+)?$/.test(amount)) throw new Error(`not a decimal: ${amount}`);
  const [whole = "0", fraction = ""] = amount.split(".");
  if (fraction.length > decimals) {
    throw new Error(`more than ${decimals} decimal places: ${amount}`);
  }
  const digits = `${whole}${fraction.padEnd(decimals, "0")}`.replace(/^0+(?=\d)/, "");
  return digits;
}

/**
 * Base units to a human amount, capped at 6 fraction digits with trailing zeros
 * trimmed: the same rule as the app's `formatTokenAmount`, so the console's
 * "~49.884202 USDT" is the string the member will see for the same quote.
 */
export function formatBaseUnits(base: string, decimals: number): string {
  const padded = base.padStart(decimals + 1, "0");
  const whole = padded.slice(0, padded.length - decimals).replace(/^0+(?=\d)/, "");
  const fraction = padded.slice(padded.length - decimals).slice(0, 6).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
}

/** A balance or amount for display: grouped, at most 6 places. Never what gets sent. */
export function formatAmount(amount: string): string {
  const n = Number(amount);
  return Number.isFinite(n) ? n.toLocaleString("en-US", { maximumFractionDigits: 6 }) : amount;
}

/** "$49.73", the app's `formatUsd`. */
export function formatUsd(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Dollars typed by staff to a sell-token amount, at most 6 fraction digits (and
 * never more than the token has). This amount is what gets sent and what the
 * member's title reads, so it is kept legible; 0.000001 BTC is about a dime.
 */
export function tokenAmountFromFiat(fiat: string, price: number, decimals: number): string {
  const usd = Number(fiat);
  if (!Number.isFinite(usd) || usd <= 0 || !(price > 0)) return "";
  const places = Math.min(decimals, 6);
  const fixed = (usd / price).toFixed(places);
  const trimmed = fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed;
  return trimmed === "0" ? "" : trimmed;
}

export interface SwapEstimateRequest {
  fromChain: number;
  toChain: number;
  fromToken: string;
  toToken: string;
  /** Base units. */
  fromAmount: string;
}

export interface SwapEstimate {
  /** Base units of the buy token. */
  toAmount: string;
  /** LI.FI's own USD figure for the output, or null when it gives none. */
  toAmountUsd: number | null;
}

export type EstimateFn = (req: SwapEstimateRequest) => Promise<SwapEstimate>;

const LIFI_QUOTE_URL = "https://li.quest/v1/quote";

/**
 * A quote needs a sender. The member's wallet is not needed for a size check,
 * and quoting against it would put a staff lookup in LI.FI's logs under the
 * member's address. The burn address quotes identically (probed 2026-09-25,
 * docs/references/lifi/probes/).
 */
const ESTIMATE_SENDER = "0x000000000000000000000000000000000000dEaD";

/** Same slippage the app defaults to (100 bps), so the estimate matches. */
const SLIPPAGE = "0.01";

export const fetchLifiEstimate: EstimateFn = async (req) => {
  const url = new URL(LIFI_QUOTE_URL);
  url.searchParams.set("fromChain", String(req.fromChain));
  url.searchParams.set("toChain", String(req.toChain));
  url.searchParams.set("fromToken", req.fromToken);
  url.searchParams.set("toToken", req.toToken);
  url.searchParams.set("fromAmount", req.fromAmount);
  url.searchParams.set("fromAddress", ESTIMATE_SENDER);
  url.searchParams.set("slippage", SLIPPAGE);
  url.searchParams.set("integrator", "nerona");

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`LI.FI quote failed (${res.status})`);
  const json = (await res.json()) as {
    estimate?: { toAmount?: unknown; toAmountUSD?: unknown };
  };
  const toAmount = json.estimate?.toAmount;
  if (typeof toAmount !== "string" || !/^\d+$/.test(toAmount)) {
    throw new Error("LI.FI quote had no toAmount");
  }
  const usd = Number(json.estimate?.toAmountUSD);
  return { toAmount, toAmountUsd: Number.isFinite(usd) ? usd : null };
};
